const registered = new Set();
const cleanups = new Map();

/**
 * Runs fn() exactly once per key for the lifetime of the JS context.
 * If fn returns:
 *  - a function => stored as cleanup
 *  - a Capacitor listener handle with .remove() => stored as cleanup
 */
export async function registerOnce(key, fn) {
  const k = String(key || '').trim();
  if (!k) return await fn?.();

  if (registered.has(k)) {
    return cleanups.get(k) || null;
  }

  registered.add(k);

  try {
    const res = await fn?.();

    // Normalize cleanup
    let cleanup = null;

    if (typeof res === 'function') {
      cleanup = res;
    } else if (res && typeof res.remove === 'function') {
      cleanup = () => res.remove();
    }

    cleanups.set(k, cleanup);

    if (process.env.NODE_ENV !== 'production') {
      console.log('[registerOnce] registered', k);
    }

    return cleanup;
  } catch (err) {
    registered.delete(k);
    cleanups.delete(k);
    throw err;
  }
}

/** Optional: allow manual cleanup if ever needed (not used by default). */
export async function unregisterOnce(key) {
  const k = String(key || '').trim();
  const cleanup = cleanups.get(k);
  if (cleanup) {
    try { await cleanup(); } catch {}
  }
  registered.delete(k);
  cleanups.delete(k);
}
